import { Selector } from 'react-redux';
import { RootState } from '../root-reducer';
import { createSelector } from 'reselect';
import { ICheckoutInitialState, ICheckoutItem, ICurrencyInfo, IFeeCalculationResponse } from './types';
import { ISaleObject } from '../../types/global/helper';

export const checkoutInitialStateSelector: Selector<RootState, ICheckoutInitialState> = createSelector(
    (state: RootState) => state.checkout,
    checkout => checkout,
);

export const checkoutItemsSelector: Selector<RootState, ICheckoutItem[]> = createSelector(
    checkoutInitialStateSelector,
    checkout => checkout.items,
);

export const checkoutIsOpenSelector: Selector<RootState, boolean> = createSelector(
    checkoutInitialStateSelector,
    checkout => checkout.isOpen,
);

export const checkoutDiscountSelector = createSelector(checkoutInitialStateSelector, checkout => checkout.discount);

export const checkoutSelectedPaymentMethodSelector = createSelector(
    checkoutInitialStateSelector,
    checkout => checkout.selectedPaymentMethod,
);

export const checkoutProcessingSelector = createSelector(checkoutInitialStateSelector, checkout => checkout.processing);

export const checkoutErrorSelector = createSelector(checkoutInitialStateSelector, checkout => checkout.error);

export const checkoutSuccessSelector = createSelector(checkoutInitialStateSelector, checkout => checkout.success);

/** This selector here works on the currency that is local to the user even if the processing happens
 * with a different currency
 */
export const checkoutSubtotalSelector = createSelector(checkoutItemsSelector, items =>
    items.reduce((sum, item) => sum + item.price * item.quantity, 0),
);

/**
 * This also focuses on the currency that is local to the user and not used
 * for final processng
 */
export const checkoutTotalSelector = createSelector(
    [checkoutSubtotalSelector, checkoutDiscountSelector],
    (subtotal, discount) => {
        if (!discount) return subtotal;

        if (discount.type === 'percentage') {
            return subtotal - (subtotal * discount.value) / 100;
        } else if (discount.type === 'fixed') {
            return Math.max(0, subtotal - discount.value);
        }

        return subtotal;
    },
);

export const checkoutCurrencySelector = (state: RootState): string => state.checkout.currency || 'usd';

export const checkoutProcessingSubTotalSelector = createSelector(checkoutItemsSelector, items =>
    items.reduce((sum, item) => sum + item.original_price * item.quantity, 0),
);

export const checkoutProcessingTotalSelector = createSelector(
    [checkoutProcessingSubTotalSelector, checkoutDiscountSelector],
    (subtotal, discount) => {
        if (!discount) return subtotal;

        if (discount.type === 'percentage') {
            return subtotal - (subtotal * discount.value) / 100;
        } else if (discount.type === 'fixed') {
            return Math.max(0, subtotal - discount.value);
        }

        return subtotal;
    },
);

export const currencyInfoSelector = createSelector([checkoutInitialStateSelector], checkout => checkout.currencyInfo);

export const processingCurrencySelector = (state: RootState): string =>
    state.checkout.currencyInfo?.original_currency || 'usd';

export const currentSaleObjectSelector: Selector<RootState, ISaleObject | undefined> = createSelector(
    [checkoutInitialStateSelector],
    checkout => checkout.saleObject,
);

export const currencyInfoObjectSelector: Selector<RootState, ICurrencyInfo | undefined> = createSelector(
    [checkoutInitialStateSelector],
    checkout => checkout.currencyInfo,
);

export const checkoutFeeCalculationSelector: Selector<RootState, IFeeCalculationResponse | undefined> = createSelector(
    checkoutInitialStateSelector,
    checkout => checkout.feeCalculation,
);

export const checkoutCalculatingFeesSelector: Selector<RootState, boolean> = createSelector(
    checkoutInitialStateSelector,
    checkout => checkout.calculatingFees,
);
